"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const MAX_N = 170;

function permutations(n: number, r: number): number {
  let result = 1;
  for (let i = 0; i < r; i++) {
    result *= n - i;
  }
  return result;
}

function combinations(n: number, r: number): number {
  const k = Math.min(r, n - r);
  let result = 1;
  for (let i = 1; i <= k; i++) {
    result = (result * (n - k + i)) / i;
  }
  return result;
}

function formatBig(x: number, formatNum: NumberFormatter): string {
  if (x < 1e15) {
    return formatNum(Math.round(x));
  }
  return formatNum(x, { notation: "scientific", maximumFractionDigits: 4 });
}

function productExpansion(
  n: number,
  r: number,
  emptyProductLabel: string
): string {
  if (r === 0) {
    return emptyProductLabel;
  }
  const factors: number[] = [];
  for (let i = 0; i < r; i++) {
    factors.push(n - i);
  }
  if (factors.length <= 6) {
    return factors.join(" × ");
  }
  const head = factors.slice(0, 3).join(" × ");
  const last = factors.at(-1);
  return `${head} × … × ${last}`;
}

function parseIntInRange(value: string): number | null {
  const n = parseIntegerInput(value);
  if (n === null || n < 0 || n > MAX_N) {
    return null;
  }
  return n;
}

function NumberField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="block font-medium text-foreground text-sm" htmlFor={id}>
        {label}
      </label>
      <input
        className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        id={id}
        max={MAX_N}
        min={0}
        onChange={(e) => onChange(e.target.value)}
        type="number"
        value={value}
      />
    </div>
  );
}

function ResultCard({
  title,
  formula,
  working,
  value,
}: {
  title: string;
  formula: string;
  working: string[];
  value: string;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-5">
      <p className="font-medium text-foreground">{title}</p>
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="font-mono text-muted-foreground text-sm">{formula}</p>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-3 font-mono text-muted-foreground text-sm leading-relaxed">
        {working.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const PERMUTATION_COMBINATION_CALCULATOR_STRINGS = {
  npAndNcr: "nPr and nCr",
  totalItemsN: "Total items (n)",
  itemsChosenR: "Items chosen (r)",
  emptyProduct: "1 (empty product)",
  permutationsTitle: "Permutations — order matters ({n}P{r})",
  permutationsWorking:
    "one:{n}! ÷ {rest}! cancels down to the product of the top {count} factor:|other:{n}! ÷ {rest}! cancels down to the product of the top {count} factors:",
  combinationsTitle: "Combinations — order ignored ({n}C{r})",
  combinationsWorking:
    "{n}C{r} = {n}P{r} ÷ {r}! — divide out the {orderings} orderings of each selection:",
  errorMessage:
    "Enter whole numbers with 0 ≤ r ≤ n ≤ {max}. Larger factorials overflow ordinary floating-point numbers.",
  whenDoesOrderMatter: "When does order matter?",
  orderMattersHeading: "Order matters → permutation (nPr).",
  orderMattersBody:
    "A lock code is the classic example: on a 3-digit lock using the digits 1, 2, and 3, the codes 1-2-3 and 3-2-1 are different — the same three digits in a different order will not open the lock. Race podiums (1st, 2nd, 3rd), passwords, and seating arrangements all work the same way.",
  orderDoesNotMatterHeading: "Order does not matter → combination (nCr).",
  orderDoesNotMatterBody:
    "Pizza toppings are the classic example: a pizza with mushrooms, olives, and peppers is exactly the same pizza as one with peppers, olives, and mushrooms. Committees, lottery number picks, and hands of cards are combinations too.",
  whyNcrNeverLarger:
    "That is why nCr is never larger than nPr: every unordered selection of r items corresponds to r! different ordered arrangements, so nCr = nPr ÷ r!.",
} as const;

export default function PermutationCombinationCalculatorWidget() {
  const [nInput, setNInput] = useState("10");
  const [rInput, setRInput] = useState("3");

  const strings = useToolUi(PERMUTATION_COMBINATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const formatNum = useToolNumber();

  const n = parseIntInRange(nInput);
  const r = parseIntInRange(rInput);
  const valid = n !== null && r !== null && r <= n;

  return (
    <>
      <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.npAndNcr}
        </h2>
        <div className="flex flex-wrap items-end gap-6">
          <NumberField
            id="pc-n"
            label={strings.totalItemsN}
            onChange={setNInput}
            value={nInput}
          />
          <NumberField
            id="pc-r"
            label={strings.itemsChosenR}
            onChange={setRInput}
            value={rInput}
          />
        </div>
        {valid && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ResultCard
              formula={`${n}P${r} = ${n}! ÷ (${n} − ${r})! = ${n}! ÷ ${n - r}!`}
              title={t(strings.permutationsTitle, { n, r })}
              value={formatBig(permutations(n, r), formatNum)}
              working={[
                tPlural(strings.permutationsWorking, locale, r, {
                  n,
                  rest: n - r,
                }),
                `${productExpansion(n, r, strings.emptyProduct)} = ${formatBig(permutations(n, r), formatNum)}`,
              ]}
            />
            <ResultCard
              formula={`${n}C${r} = ${n}! ÷ (${r}! × (${n} − ${r})!) = ${n}! ÷ (${r}! × ${n - r}!)`}
              title={t(strings.combinationsTitle, { n, r })}
              value={formatBig(combinations(n, r), formatNum)}
              working={[
                t(strings.combinationsWorking, {
                  n,
                  r,
                  orderings: formatBig(permutations(r, r), formatNum),
                }),
                `${formatBig(permutations(n, r), formatNum)} ÷ ${formatBig(permutations(r, r), formatNum)} = ${formatBig(combinations(n, r), formatNum)}`,
              ]}
            />
          </div>
        )}
        {!valid && (
          <p className="text-muted-foreground text-sm">
            {t(strings.errorMessage, { max: MAX_N })}
          </p>
        )}
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.whenDoesOrderMatter}
        </h2>
        <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <p>
            <strong className="text-foreground">
              {strings.orderMattersHeading}
            </strong>{" "}
            {strings.orderMattersBody}
          </p>
          <p>
            <strong className="text-foreground">
              {strings.orderDoesNotMatterHeading}
            </strong>{" "}
            {strings.orderDoesNotMatterBody}
          </p>
          <p>{strings.whyNcrNeverLarger}</p>
        </div>
      </section>
    </>
  );
}
